import { useEffect, useState } from "react";
import { Link } from "react-router";
import { getSupabaseClient } from "../providers/supabase/supabase";
import "./crm360.css";

type AccountMetric = {
  key: string;
  label: string;
  value: number | null;
  href: string | null;
};

type ActivityItem = {
  id: number;
  activity_type: string;
  subject: string;
  description: string | null;
  occurred_at: string;
};

type CRM360PanelProps =
  | { kind: "account"; recordId: string | number }
  | { kind: "contact"; recordId: string | number };

const ACTIVITY_LABELS: Record<string, string> = {
  call: "电话",
  meeting: "会议",
  email: "邮件",
  visit: "拜访",
  task: "任务",
  note: "备注",
  other: "其他",
};

export const CRM360Panel = (props: CRM360PanelProps) => {
  const [metrics, setMetrics] = useState<AccountMetric[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setHasError(false);
      try {
        const supabase = getSupabaseClient();
        if (props.kind === "account") {
          const id = props.recordId;
          const specs = [
            { key: "contacts", label: "联系人", table: "contacts", href: "/contacts", column: "company_id" },
            { key: "deals", label: "商机", table: "deals", href: "/deals", column: "company_id" },
            { key: "activities", label: "活动记录", table: "activities", href: null, column: "company_id" },
            { key: "quotes", label: "报价", table: "quotes", href: null, column: "company_id" },
            { key: "contracts", label: "合同", table: "contracts", href: null, column: "company_id" },
            { key: "cases", label: "服务工单", table: "service_cases", href: null, column: "company_id" },
          ];
          const results = await Promise.all(specs.map(async (spec) => {
            try {
              const result = await supabase.from(spec.table).select("id", { count: "exact", head: true }).eq(spec.column, id);
              return { spec, count: result.error ? null : result.count };
            } catch {
              return { spec, count: null };
            }
          }));
          if (!cancelled) {
            setMetrics(results.map(({ spec, count }) => ({
              key: spec.key,
              label: spec.label,
              value: count,
              href: spec.href,
            })));
            setHasError(results.some(({ count }) => count === null));
          }
        } else {
          const { data, error } = await supabase
            .from("activities")
            .select("id,activity_type,subject,description,occurred_at")
            .eq("contact_id", props.recordId)
            .order("occurred_at", { ascending: false })
            .limit(8);
          if (!cancelled) {
            setActivities((data ?? []) as ActivityItem[]);
            setHasError(Boolean(error));
          }
        }
      } catch {
        if (!cancelled) {
          setHasError(true);
          if (props.kind === "account") {
            setMetrics([
              { key: "contacts", label: "联系人", value: null, href: "/contacts" },
              { key: "deals", label: "商机", value: null, href: "/deals" },
              { key: "activities", label: "活动记录", value: null, href: null },
              { key: "quotes", label: "报价", value: null, href: null },
              { key: "contracts", label: "合同", value: null, href: null },
              { key: "cases", label: "服务工单", value: null, href: null },
            ]);
          } else {
            setActivities([]);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [props.kind, props.recordId]);

  if (props.kind === "account") {
    return (
      <section className="orbitas-360" aria-label="客户360全景">
        <div className="orbitas-360__heading">
          <div>
            <p className="orbitas-360__eyebrow">客户视图</p>
            <h3>客户 360° 全景</h3>
            <p className="orbitas-360__description">汇总客户关系、销售机会与服务记录。未接入或无权读取的数据会显示为“—”。</p>
          </div>
        </div>
        <div className="orbitas-360__metrics">
          {metrics.map((metric) => {
            const content = (
              <>
                <span>{metric.label}</span>
                <strong>{loading ? "…" : metric.value === null ? "—" : metric.value.toLocaleString("zh-CN")}</strong>
                <small>{metric.value === null ? "暂不可用" : metric.href ? "查看记录 ↗" : "概览指标"}</small>
              </>
            );
            return metric.href ? (
              <Link key={metric.key} to={metric.href} className="orbitas-360__metric">{content}</Link>
            ) : (
              <div key={metric.key} className="orbitas-360__metric orbitas-360__metric--static">{content}</div>
            );
          })}
        </div>
        {hasError && !loading ? <p className="orbitas-360__notice" role="status">部分模块暂时无法读取。请检查预览数据库迁移与访问策略；未读取的数据不会按 0 计数。</p> : null}
      </section>
    );
  }

  return (
    <section className="orbitas-360" aria-label="联系人活动时间线">
      <div className="orbitas-360__heading">
        <div>
          <p className="orbitas-360__eyebrow">关系记录</p>
          <h3>最近活动</h3>
          <p className="orbitas-360__description">展示关联此联系人的 CRM 活动；任务和备注仍保留在原有模块。</p>
        </div>
      </div>
      {loading ? <p className="orbitas-360__empty">正在加载活动记录…</p> : null}
      {!loading && activities.length === 0 ? (
        <p className="orbitas-360__empty">{hasError ? "活动记录暂时不可用，请检查访问策略。" : "暂无活动记录。完成电话、会议或跟进后，记录将显示在这里。"}</p>
      ) : null}
      {!loading && activities.length > 0 ? (
        <ol className="orbitas-360__timeline">
          {activities.map((activity) => (
            <li key={activity.id}>
              <span className="orbitas-360__timeline-dot" aria-hidden="true" />
              <div className="orbitas-360__timeline-content">
                <div className="orbitas-360__activity-top">
                  <strong>{activity.subject}</strong>
                  <span>{ACTIVITY_LABELS[activity.activity_type] ?? "活动"}</span>
                </div>
                {activity.description ? <p>{activity.description}</p> : null}
                <time dateTime={activity.occurred_at}>{new Date(activity.occurred_at).toLocaleString("zh-CN", { dateStyle: "medium", timeStyle: "short" })}</time>
              </div>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
};
